import { useCallback, useEffect, useRef, useState } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"
import { downloadInvoicePdf } from "../redux/invoices.thunks.ts"
import { INVOICE_ORIGINS } from "../types/invoices.types.ts"

interface PreviewDoc {
  file: string
  original_filename: string
}

const documentName = (record: ProjectInvoice): string => {
  if (record.origin === INVOICE_ORIGINS.GENERATED && record.invoice_number) {
    return `${record.invoice_number}.pdf`
  }
  return record.attachments?.[0]?.original_filename || "invoice"
}

export const useInvoiceDocumentPreview = (
  onError?: (payload: unknown) => void
) => {
  const dispatch = useAppDispatch()
  const [doc, setDoc] = useState<PreviewDoc | null>(null)
  const [loading, setLoading] = useState(false)
  const objectUrl = useRef<string | null>(null)
  const mounted = useRef(true)
  const latestRequest = useRef(0)

  const revoke = useCallback(() => {
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current)
      objectUrl.current = null
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      revoke()
    }
  }, [revoke])

  const open = useCallback(
    async (record: ProjectInvoice) => {
      const request = ++latestRequest.current
      const isStale = () => !mounted.current || request !== latestRequest.current

      setLoading(true)
      try {
        const blob = await dispatch(downloadInvoicePdf(record.id)).unwrap()
        if (isStale()) return
        revoke()
        objectUrl.current = URL.createObjectURL(blob)
        setDoc({
          file: objectUrl.current,
          original_filename: documentName(record)
        })
      } catch (payload) {
        if (isStale()) return
        revoke()
        setDoc(null)
        onError?.(payload)
      } finally {
        if (!isStale()) setLoading(false)
      }
    },
    [dispatch, onError, revoke]
  )

  const close = useCallback(() => {
    // supersedes any in-flight fetch so a late blob cannot reopen the preview
    latestRequest.current += 1
    setDoc(null)
    setLoading(false)
    revoke()
  }, [revoke])

  return { doc, loading, open, close }
}
